import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Divider,
  Select,
  MenuItem
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchStatutoryBalances,
  recordStatutoryPayment,
  fetchStatutoryPaymentHistory
} from '../../store/statutorySettlementStore/statutorySettlementThunk';
import { resetPaymentStatus } from '../../store/statutorySettlementStore/statutorySettlementSlice';
import { fetchBanks } from '../../store/bankStore/bankThunk';
import { ApiStatus } from '../../utils/api.types';
import { IStatutoryPaymentForm } from '../../utils/statutorySettlement.types';
import { Utils } from '../../utils/common-utils';
import { format } from 'date-fns';
import StatutoryPaymentDialog from './StatutoryPaymentDialog';

const StatutoryCompliance: React.FC = () => {
  const dispatch = useAppDispatch();

  const { balances, payments, balancesStatus, paymentStatus, historyStatus } = useAppSelector(
    (state) => state.statutorySettlement
  );
  const { selectedAccount } = useAppSelector((state) => state.auth);

  const { startDateFY, endDateFY } = Utils.getFinancialYearDates();

  const [dateRange, setDateRange] = useState({
    startDate: startDateFY,
    endDate: endDateFY
  });
  const [selectedDateRangeValue, setSelectedDateRangeValue] = useState('This FY Year');
  const [showDateRange, setShowDateRange] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [paymentForm, setPaymentForm] = useState<IStatutoryPaymentForm>({
    type: 'PF',
    amount: 0,
    paymentPeriod: {
      month: '',
      fromDate: '',
      toDate: '',
      period: ''
    },
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    challanNumber: '',
    referenceNumber: '',
    paymentMode: 'NEFT',
    bankAccount: '',
    remarks: ''
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Fetch bank accounts
  useEffect(() => {
    const getBanks = async () => {
      if (selectedAccount?._id) {
        try {
          const result: any = await dispatch(fetchBanks(selectedAccount._id));
          if (result.payload?.banks) {
            setBankAccounts(result.payload.banks.map((bank: any) => ({
              _id: bank._id,
              ledgerName: `${bank.accountName} - ${bank.bankName}`
            })));
          }
        } catch (error) {
          console.error('Error fetching banks:', error);
        }
      }
    };
    getBanks();
  }, [dispatch, selectedAccount]);

  // Fetch balances when dates change
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
      const toDate = format(dateRange.endDate, 'yyyy-MM-dd');
      dispatch(fetchStatutoryBalances({ fromDate, toDate }));
    }
  }, [dateRange, dispatch]);

  // Fetch payment history on mount
  useEffect(() => {
    dispatch(fetchStatutoryPaymentHistory());
  }, [dispatch]);

  // Close dialog on successful payment
  useEffect(() => {
    if (paymentStatus === ApiStatus.SUCCESS && openPaymentDialog) {
      setOpenPaymentDialog(false);
      dispatch(resetPaymentStatus());
      if (dateRange.startDate && dateRange.endDate) {
        const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
        const toDate = format(dateRange.endDate, 'yyyy-MM-dd');
        dispatch(fetchStatutoryBalances({ fromDate, toDate }));
      }
      dispatch(fetchStatutoryPaymentHistory());
    }
  }, [paymentStatus, openPaymentDialog, dispatch, dateRange]);

  const handleDateChange = (newValue: Date | null, name: string | undefined) => {
    if (newValue) {
      if (name === 'startDate') {
        setDateRange({ ...dateRange, startDate: newValue });
      } else if (name === 'endDate') {
        setDateRange({ ...dateRange, endDate: newValue });
      }
    }
  };

  const handleDateRange = (e: any) => {
    const _dateFilterName = e.target.value;
    setSelectedDateRangeValue(_dateFilterName);

    if (e.target.value && e.target.value !== '') {
      if (_dateFilterName === 'This Month') {
        setShowDateRange(false);
        const date = new Date();
        const y = date.getFullYear();
        const m = date.getMonth();
        setDateRange({
          startDate: new Date(y, m, 1),
          endDate: new Date(y, m + 1, 0)
        });
      } else if (_dateFilterName === 'Last Month') {
        setShowDateRange(false);
        const date = new Date();
        const y = date.getFullYear();
        const m = date.getMonth();
        setDateRange({
          startDate: new Date(y, m - 1, 1),
          endDate: new Date(y, (m - 1) + 1, 0)
        });
      } else if (_dateFilterName === 'This FY Year') {
        setShowDateRange(false);
        setDateRange({
          startDate: startDateFY,
          endDate: endDateFY
        });
      } else if (_dateFilterName === 'Last FY Year') {
        setShowDateRange(false);
        const { startDateLastFY, endDateLastFY } = Utils.getLastFinancialYearDates();
        setDateRange({
          startDate: startDateLastFY,
          endDate: endDateLastFY
        });
      } else if (_dateFilterName === 'Custom') {
        setShowDateRange(true);
      }
    }
  };

  const handleOpenPaymentDialog = (type: 'PF' | 'ESI' | 'PROFESSIONAL_TAX') => {
    if (!balances) return;

    const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
    const toDate = format(dateRange.endDate, 'yyyy-MM-dd');

    const typeAmounts: Record<string, number> = {
      PF: balances.pf.net,
      ESI: balances.esi.net,
      PROFESSIONAL_TAX: balances.pt.net
    };

    setPaymentForm({
      type,
      amount: typeAmounts[type] || 0,
      paymentPeriod: {
        month: format(dateRange.startDate, 'yyyy-MM'),
        fromDate,
        toDate,
        period: `${type === 'PROFESSIONAL_TAX' ? 'PT' : type} for ${format(dateRange.startDate, 'MMM yyyy')}`
      },
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      challanNumber: '',
      referenceNumber: '',
      paymentMode: 'NEFT',
      bankAccount: bankAccounts.length > 0 ? bankAccounts[0]._id : '',
      remarks: ''
    });
    setOpenPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    dispatch(resetPaymentStatus());
  };

  const handlePaymentFormChange = (field: string, value: any) => {
    setPaymentForm({ ...paymentForm, [field]: value });
  };

  const handleSubmitPayment = () => {
    dispatch(recordStatutoryPayment(paymentForm));
  };

  const handleRefresh = () => {
    if (dateRange.startDate && dateRange.endDate) {
      const fromDate = format(dateRange.startDate, 'yyyy-MM-dd');
      const toDate = format(dateRange.endDate, 'yyyy-MM-dd');
      dispatch(fetchStatutoryBalances({ fromDate, toDate }));
    }
    dispatch(fetchStatutoryPaymentHistory());
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'PF': return 'PF';
      case 'ESI': return 'ESI';
      case 'PROFESSIONAL_TAX': return 'Prof. Tax';
      default: return type;
    }
  };

  const getTypeColor = (type: string): 'primary' | 'secondary' | 'warning' => {
    switch (type) {
      case 'PF': return 'primary';
      case 'ESI': return 'secondary';
      case 'PROFESSIONAL_TAX': return 'warning';
      default: return 'primary';
    }
  };

  return (
    <Box sx={{ p: 2, mb: 2 }} className={'c-card2'}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="600">
          Statutory Compliance (PF, ESI, Professional Tax)
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Date Range Selector */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Select
              className='c-text-field-4'
              value={selectedDateRangeValue}
              onChange={handleDateRange}
              disableUnderline
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
              }}
            >
              <MenuItem value="This Month">This Month</MenuItem>
              <MenuItem value="Last Month">Last Month</MenuItem>
              <MenuItem value="This FY Year">This FY Year</MenuItem>
              <MenuItem value="Last FY Year">Last FY Year</MenuItem>
              <MenuItem value="Custom">Custom Range</MenuItem>
            </Select>
          </Grid>

          {showDateRange && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item>
                <DesktopDatePicker
                  inputFormat="dd/MM/yyyy"
                  InputProps={{
                    disableUnderline: true,
                    style: { fontSize: 14, height: 28 },
                  }}
                  renderInput={(params: any) =>
                    <TextField {...params}
                      required
                      className='c-text-field-3'
                      variant="standard"
                      sx={{ width: 150 }}
                      size='small'
                      placeholder="Start Date"
                    />}
                  value={dateRange.startDate || null}
                  onChange={(value: any) => { handleDateChange(value, 'startDate') }}
                />
              </Grid>
              <Grid item>
                <DesktopDatePicker
                  inputFormat="dd/MM/yyyy"
                  InputProps={{
                    disableUnderline: true,
                    style: { fontSize: 14, height: 28 },
                  }}
                  renderInput={(params: any) =>
                    <TextField {...params}
                      required
                      className='c-text-field-3'
                      variant="standard"
                      sx={{ width: 150 }}
                      size='small'
                      placeholder="End Date"
                    />}
                  value={dateRange.endDate || null}
                  onChange={(value: any) => { handleDateChange(value, 'endDate') }}
                />
              </Grid>
            </LocalizationProvider>
          )}

          <Grid item>
            <Typography variant="body2" color="text.secondary">
              {format(dateRange.startDate, 'dd MMM yyyy')} - {format(dateRange.endDate, 'dd MMM yyyy')}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {balancesStatus === ApiStatus.LOADING ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ============================================ */}
          {/* PF SETTLEMENT SECTION */}
          {/* ============================================ */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, backgroundColor: '#e9ecf1', p: 2, mx: -2 }}>
              <Typography variant="h6" fontWeight="600">
                PF Settlement (Provident Fund)
              </Typography>
            </Box>

            {balances && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'white', border: '2px solid #9da7b7', borderRadius: 5, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="600" color="error.main" gutterBottom>
                      PF Payable (To EPFO)
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h4" fontWeight="700" color="error.main">
                      {formatCurrency(balances.pf.payable)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'white', border: '2px solid #9da7b7', borderRadius: 5, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="600" color="success.main" gutterBottom>
                      PF Already Paid
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h4" fontWeight="700" color="success.main">
                      {formatCurrency(balances.pf.paid)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'white', border: '3px solid #9da7b7', borderRadius: 5, height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="600">
                        Net PF to Pay
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<PaymentIcon />}
                        onClick={() => handleOpenPaymentDialog('PF')}
                        color="warning"
                        size="small"
                        disabled={!balances.pf.net || balances.pf.net <= 0}
                      >
                        Record Payment
                      </Button>
                    </Box>
                    <Typography variant="h4" fontWeight="700" color="warning.main">
                      {formatCurrency(balances.pf.net)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* ============================================ */}
          {/* ESI SETTLEMENT SECTION */}
          {/* ============================================ */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, backgroundColor: '#e9ecf1', p: 2, mx: -2 }}>
              <Typography variant="h6" fontWeight="600">
                ESI Settlement (Employee State Insurance)
              </Typography>
            </Box>

            {balances && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'white', border: '2px solid #9da7b7', borderRadius: 5, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="600" color="error.main" gutterBottom>
                      ESI Payable (To ESIC)
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h4" fontWeight="700" color="error.main">
                      {formatCurrency(balances.esi.payable)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'white', border: '2px solid #9da7b7', borderRadius: 5, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="600" color="success.main" gutterBottom>
                      ESI Already Paid
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h4" fontWeight="700" color="success.main">
                      {formatCurrency(balances.esi.paid)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'white', border: '3px solid #9da7b7', borderRadius: 5, height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="600">
                        Net ESI to Pay
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<PaymentIcon />}
                        onClick={() => handleOpenPaymentDialog('ESI')}
                        color="warning"
                        size="small"
                        disabled={!balances.esi.net || balances.esi.net <= 0}
                      >
                        Record Payment
                      </Button>
                    </Box>
                    <Typography variant="h4" fontWeight="700" color="warning.main">
                      {formatCurrency(balances.esi.net)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* ============================================ */}
          {/* PROFESSIONAL TAX SETTLEMENT SECTION */}
          {/* ============================================ */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, backgroundColor: '#e9ecf1', p: 2, mx: -2 }}>
              <Typography variant="h6" fontWeight="600">
                Professional Tax Settlement
              </Typography>
            </Box>

            {balances && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'white', border: '2px solid #9da7b7', borderRadius: 5, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="600" color="error.main" gutterBottom>
                      PT Payable (To State Govt)
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h4" fontWeight="700" color="error.main">
                      {formatCurrency(balances.pt.payable)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'white', border: '2px solid #9da7b7', borderRadius: 5, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="600" color="success.main" gutterBottom>
                      PT Already Paid
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h4" fontWeight="700" color="success.main">
                      {formatCurrency(balances.pt.paid)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'white', border: '3px solid #9da7b7', borderRadius: 5, height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="600">
                        Net PT to Pay
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<PaymentIcon />}
                        onClick={() => handleOpenPaymentDialog('PROFESSIONAL_TAX')}
                        color="warning"
                        size="small"
                        disabled={!balances.pt.net || balances.pt.net <= 0}
                      >
                        Record Payment
                      </Button>
                    </Box>
                    <Typography variant="h4" fontWeight="700" color="warning.main">
                      {formatCurrency(balances.pt.net)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* ============================================ */}
          {/* PAYMENT HISTORY TABLE */}
          {/* ============================================ */}
          <Box sx={{ p: 2, bgcolor: 'white', border: '1px solid #9da7b7', borderRadius: 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" fontWeight="600">
                Statutory Payment History
              </Typography>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                size="small"
                onClick={() => dispatch(fetchStatutoryPaymentHistory())}
              >
                Refresh History
              </Button>
            </Box>
            <Divider sx={{ my: 1 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Txn ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Payment Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Period</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Challan No.</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Amount Paid</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Payment Mode</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem', py: 1.5 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyStatus === ApiStatus.LOADING ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No statutory payment records found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((payment) => (
                      <TableRow key={payment._id} hover>
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            label={getTypeLabel(payment.type)}
                            size="medium"
                            variant="outlined"
                            color={getTypeColor(payment.type)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.txnId}</TableCell>
                        <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{format(new Date(payment.paymentDate), 'dd MMM yyyy')}</TableCell>
                        <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.paymentPeriod?.period || payment.paymentPeriod?.month || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.challanNumber || '-'}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem', py: 1.5 }}>{formatCurrency(payment.amount)}</TableCell>
                        <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>{payment.paymentMode || '-'}</TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            label={payment.status}
                            color={payment.status === 'PAID' ? 'success' : 'default'}
                            size="medium"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}

      {/* Payment Dialog */}
      <StatutoryPaymentDialog
        open={openPaymentDialog}
        onClose={handleClosePaymentDialog}
        paymentForm={paymentForm}
        onPaymentFormChange={handlePaymentFormChange}
        onSubmit={handleSubmitPayment}
        paymentStatus={paymentStatus}
        bankLedgers={bankAccounts}
      />
    </Box>
  );
};

export default StatutoryCompliance;
